import { useMemo, type ComponentProps } from 'react'
import { NavMain, findNavItemByPin } from './nav-main'
import { NavUser } from './nav-user'
import { SidebarBrand } from './sidebar-brand'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from './ui/sidebar'
import navigationConfig from '@/client/config/navigation'
import { filterNavigation } from '@/client/config/featureFlags'
import { useUserStore } from '@/client/stores/user'
import { useNavPinsSync } from '@/client/hooks/useNavPinsSync'
import { useSidebarNavStore } from '@/client/stores/sidebar-nav'
import { resolvePermissionForPath } from '@/client/config/permissions'
import { useIsSuperAdmin } from '@/client/hooks/useIsSuperAdmin'
import type { NavigationItem } from '@/client/types/admin/types'

// The code an item requires: explicit `item.permission` wins, else derived from
// its url via the route guard's resolver — so a link hides exactly when its
// page would deny access. `null` is explicitly open. Unmapped urls are hidden
// unless the caller is a superadmin. `#` headers carry no code of their own.
function requiredCode(item: NavigationItem): string | null | undefined {
  if (item.permission) return item.permission
  if (!item.url || item.url === '#') return undefined
  return resolvePermissionForPath(item.url.split('?')[0])
}

// Drop nav items whose required code the user lacks (superadmins keep all).
// A `#` header left with no surviving children is dropped too.
function filterByPermission(
  items: NavigationItem[],
  can: (code: string) => boolean,
  isSuperAdmin: boolean,
): NavigationItem[] {
  return items.reduce<NavigationItem[]>((acc, item) => {
    const code = requiredCode(item)
    const concrete = !!item.url && item.url !== '#'
    if (concrete && code === undefined && !isSuperAdmin) return acc
    if (code && !can(code)) return acc

    if (item.items) {
      const children = filterByPermission(item.items, can, isSuperAdmin)
      // A header with no own destination and no surviving children is noise.
      if (children.length === 0 && (!item.url || item.url === '#')) return acc
      acc.push({ ...item, items: children })
    } else {
      acc.push(item)
    }
    return acc
  }, [])
}

export function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  const { user } = useUserStore()
  useNavPinsSync(!!user)

  const pins = useSidebarNavStore((s) => s.pins)

  // Superadmin (administrator_super role) and the effective permission codes
  // both come from the session user (users/info) — no extra fetch.
  const { isSuperAdmin } = useIsSuperAdmin()
  const permissionCodes = useMemo(
    () => new Set(user?.permissions ?? []),
    [user?.permissions],
  )
  const canAccess = useMemo(
    () => (code: string) => isSuperAdmin || permissionCodes.has(code),
    [isSuperAdmin, permissionCodes],
  )

  // Hide feature-flag-disabled and permission-gated routes from the sidebar
  // (coming-soon stays).
  const navItems = useMemo(
    () => filterNavigation(filterByPermission(navigationConfig, canAccess, isSuperAdmin)),
    [canAccess, isSuperAdmin],
  )

  // Overview stays fixed at the top; everything else is A–Z under Favorites.
  const overviewItem = useMemo(
    () => navItems.find((item) => item.title === 'Overview') ?? null,
    [navItems],
  )

  const sortedItems = useMemo(
    () =>
      navItems
        .filter((item) => item.title !== 'Overview')
        .sort((a, b) => a.title.localeCompare(b.title)),
    [navItems],
  )

  // Only first-level items are pinnable. Keys are URLs for real links or
  // `nav:Business` for section headers. Legacy title pins still match.
  const favorites = useMemo(() => {
    return pins
      .map((pin) => {
        const match = findNavItemByPin(navItems, pin)
        return match ? { ...match, pinKey: pin } : null
      })
      .filter(Boolean) as typeof navItems
  }, [navItems, pins])

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
  const displayName =
    fullName || user?.username || user?.preferred_name || 'User'

  const sidebarUser = {
    name: displayName,
    email: user?.email1 as string,
    avatar: '/assets/common/logo.png',
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarBrand />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={sortedItems} favorites={favorites} overview={overviewItem} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={sidebarUser} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
